import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Cross, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const MAX = 500;

export default function Eucaristia() {
  const [form, setForm] = useState({ nombre: "", acudiente_de: "", curso: "", intencion: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.nombre || !form.acudiente_de || !form.curso || !form.intencion) {
      toast.error("Por favor completa todos los campos.");
      return;
    }
    if (form.intencion.length > MAX) {
      toast.error(`La intención debe tener máximo ${MAX} caracteres.`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/intenciones", form);
      toast.success("¡Gracias! Tu intención fue recibida.");
      setSent(true);
      setForm({ nombre: "", acudiente_de: "", curso: "", intencion: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la intención");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="eucaristia-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-muted/40 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-16 text-center">
          <span className="inline-flex w-14 h-14 rounded-full bg-primary text-primary-foreground items-center justify-center mb-5">
            <Cross className="w-6 h-6" />
          </span>
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-3">Celebración de la Eucaristía</p>
          <h1 className="font-display text-4xl md:text-6xl text-primary leading-[1.05] max-w-3xl mx-auto" data-testid="eucaristia-title">
            Oramos por tus intenciones en la sagrada Eucaristía.
          </h1>
          <p className="mt-5 font-display text-xl md:text-2xl italic text-foreground/70 max-w-2xl mx-auto">
            (Acción de gracias, fieles difuntos, proyectos, entre otras).
          </p>
        </div>
      </section>

      {/* Formulario */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-10 pb-16">
        <form
          onSubmit={submit}
          className="max-w-2xl mx-auto rounded-2xl bg-card border border-border p-6 md:p-8 space-y-5 shadow-sm"
          data-testid="eucaristia-form"
        >
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Nombre completo</label>
            <Input
              className="mt-1"
              value={form.nombre}
              onChange={(e) => setField("nombre", e.target.value)}
              placeholder="Tu nombre"
              required
              data-testid="int-nombre"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Acudiente de</label>
              <Input
                className="mt-1"
                value={form.acudiente_de}
                onChange={(e) => setField("acudiente_de", e.target.value)}
                placeholder="Nombre del estudiante"
                required
                data-testid="int-acudiente"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Curso</label>
              <Input
                className="mt-1"
                value={form.curso}
                onChange={(e) => setField("curso", e.target.value)}
                placeholder="Ej. 5°B"
                required
                data-testid="int-curso"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Escribe aquí tu intención</label>
              <span className="text-xs text-foreground/50" data-testid="int-counter">{form.intencion.length}/{MAX}</span>
            </div>
            <Textarea
              className="mt-1"
              rows={6}
              maxLength={MAX}
              value={form.intencion}
              onChange={(e) => setField("intencion", e.target.value.slice(0, MAX))}
              placeholder="Comparte aquí tu intención: acción de gracias, fieles difuntos, proyectos, salud, familia…"
              required
              data-testid="int-text"
            />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
            {sent && (
              <p className="text-sm text-primary font-semibold flex items-center gap-2" data-testid="int-success">
                <CheckCircle2 className="w-4 h-4" /> Intención registrada — gracias por orar con nosotros.
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="rounded-full px-7 h-11 ml-auto"
              data-testid="int-submit"
            >
              <Send className="w-4 h-4 mr-2" /> {busy ? "Enviando…" : "Enviar intención"}
            </Button>
          </div>
        </form>
      </section>

      {/* Versículo */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 pb-20">
        <blockquote className="max-w-3xl mx-auto rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 text-center" data-testid="eucaristia-verse">
          <p className="font-display text-xl md:text-2xl italic leading-relaxed">
            “Y yo les digo: Pidan, y se les dará; busquen, y encontrarán; llamen, y se les abrirá. Porque todo aquel que pide, recibe; y el que busca, encuentra; y al que llama, se le abrirá”.
          </p>
          <p className="mt-4 text-sm tracking-[0.2em] uppercase font-semibold text-secondary">Lc 11, 9-10</p>
        </blockquote>
      </section>
    </div>
  );
}
